import {
  type BiddingBlockApprovalRequest,
  BiddingBlockBidStatus,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { BlockBidRow } from './blockBidRow'
import { blockGroupLegs } from './blockGroupLegs'

// 入札締切まで提出しなかった下書き・承認待ち・差戻しの group は、 約定軸が失効 (EXPIRED) になり、
// 承認ステータスは N/A (下書きと同じ値) に戻る (AC-BSP-099)。 承認ステータスが下書きかどうかだけでは
// 編集・削除・提出できる下書きと区別できないので、 下書きの判定は約定軸が失効でないことも見る。

/** 入札締切まで提出せず失効にした group か (AC-BSP-099)。 失効は全 leg に同時に付く。 */
export const isExpiredBlockGroup = (
  group: BiddingBlockApprovalRequest,
): boolean =>
  blockGroupLegs(group).some(
    (leg) => leg.bidStatus === BiddingBlockBidStatus.EXPIRED,
  )

/** 編集・削除・提出できる下書きの行か。 失効にした行は承認ステータスが下書きでも含めない。 */
export const isNonExpiredDraftBlockRow = (
  row: Pick<BlockBidRow, 'approvalState' | 'groupExpired'>,
): boolean =>
  row.approvalState === BiddingApprovalState.DRAFT && !row.groupExpired

/** 編集・削除・提出できる下書きの group か (明細)。 失効にした group は承認ステータスが下書きでも含めない。 */
export const isNonExpiredDraftBlockGroup = (
  group: BiddingBlockApprovalRequest,
): boolean =>
  group.approvalState === BiddingApprovalState.DRAFT &&
  !isExpiredBlockGroup(group)

/**
 * 一覧 item のうち、 編集・削除・提出できる下書きの group。 一覧 item は約定軸を持たないので、
 * 失効かどうかは明細 (`details`) と id で突き合わせて決める。 明細がまだ無い下書きは外さない
 * (その間の提出は明細の未解決で止まる)。
 */
export const selectNonExpiredDraftBlockGroups = <
  Item extends { id: string; approvalState: BiddingApprovalState },
>(
  items: readonly Item[],
  details: readonly BiddingBlockApprovalRequest[],
): Item[] => {
  const expiredIds = new Set(
    details.filter(isExpiredBlockGroup).map((detail) => detail.id),
  )
  return items.filter(
    (item) =>
      item.approvalState === BiddingApprovalState.DRAFT &&
      !expiredIds.has(item.id),
  )
}
